import React, { useEffect, useState } from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  CircularProgress,
  Divider,
  Alert,
} from '@mui/material';
import { Category as CategoryIcon } from '@mui/icons-material';
import { SubCategory, getSubCategories } from '../../api/storeApi';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedCategory, selectSelectedCategoryId } from '../../store/slices/categorySlice';
import { useNavigate } from 'react-router-dom';

interface CategoryListProps {
  onCategorySelect?: () => void;
}

const CategoryList: React.FC<CategoryListProps> = ({ onCategorySelect }) => {
  const [categories, setCategories] = useState<SubCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedCategoryId = useSelector(selectSelectedCategoryId);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        setError(null);
        const data = await getSubCategories();
        setCategories(data);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        setError('Failed to load categories. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, []);

  const handleCategoryClick = (categoryId: number | null) => {
    dispatch(setSelectedCategory(categoryId));
    if (categoryId) {
      const params = new URLSearchParams();
      params.set('category', categoryId.toString());
      navigate(`/products?${params.toString()}`);
    } else {
      navigate('/products');
    }
    onCategorySelect?.();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: 280, pt: 2 }}>
      <Typography variant="h6" sx={{ px: 2, mb: 2 }}>
        Categories
      </Typography>
      <Divider />
      <List sx={{ pt: 0 }}>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={() => handleCategoryClick(null)}
            selected={selectedCategoryId === null}
          >
            <ListItemIcon>
              <CategoryIcon />
            </ListItemIcon>
            <ListItemText primary="All Products" />
          </ListItemButton>
        </ListItem>
        <Divider />
        {categories.map((category) => (
          <ListItem key={category.id} disablePadding>
            <ListItemButton 
              onClick={() => handleCategoryClick(category.id)}
              selected={selectedCategoryId === category.id}
            >
              <ListItemIcon>
                <CategoryIcon />
              </ListItemIcon>
              <ListItemText primary={category.name} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default CategoryList;
